import { NgIf, NgFor, NgClass } from '@angular/common';
import { Component, Input, Output, EventEmitter, forwardRef, SimpleChanges, OnChanges } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { NiIconComponent } from '../ni-icon/ni-icon.component';
import { generateSecureRandomString } from '../../../../shared/utils/random-utils';

@Component({
  selector: 'NiFormCheckbox',
  standalone: true,
  imports: [NgIf, NgFor, NgClass, NiIconComponent],
  templateUrl: './ni-checkbox.component.html',
  providers: [{ provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => NiCheckboxComponent), multi: true }]
})

export class NiCheckboxComponent implements OnChanges {
  @Input() label: string = '';
  @Input() checkboxList: Array<CheckboxOption> = [];
  @Input() valueList: Array<string> = [];
  @Input() disabled: boolean = false;
  @Input() required: boolean = false;
  @Input() checked: boolean = false;
  @Input() errorMessage : string = ''
  @Input() labelPosition: 'top' | 'right' = 'right';
  @Input() id: string = `checkbox-${generateSecureRandomString(7)}`;
  @Output() onCheck: EventEmitter<Array<CheckboxOption>> = new EventEmitter<Array<CheckboxOption>>();
  @Output() onChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  ngOnChanges(changes: SimpleChanges) {
    // For single checkbox, only use checked input, ignore valueList input (form control handles it via writeValue)
    if (!this.checkboxList || this.checkboxList.length === 0) {
      // Only handle checked input changes for single checkbox
      if (changes['checked']) {
        this.checked = changes['checked'].currentValue === true || changes['checked'].currentValue === 'true';
      }
      return;
    }
    
    // Handle multiple checkboxes - use valueList input
    if (changes['valueList']) {
      const valueList = changes['valueList'].currentValue;
      // Create a new array copy to avoid mutating frozen arrays
      // Ensure valueList is an array before spreading (handle null, undefined, or non-array values)
      this.valueList = Array.isArray(valueList) ? [...valueList] : [];
      // Only update checkboxList if it exists and has items
      if (this.checkboxList && this.checkboxList.length > 0) {
        this.checkboxList = this.checkboxList.map(checkbox => ({ ...checkbox, checked: this.valueList.includes(checkbox.value) }));
      }
    }
  }

  private onFormChange: (value: boolean | string[]) => void = () => {};
  private onTouched = () => {};

  writeValue(valueList: any) {
    // Handle single checkbox (no checkboxList)
    if (!this.checkboxList || this.checkboxList.length === 0) {
      // For single checkbox, valueList can be array, boolean, or any value
      // Check if value represents true
      const isTrue = valueList === true || 
                     valueList === 'true' || 
                     (Array.isArray(valueList) && valueList.includes('true')) ||
                     (Array.isArray(valueList) && valueList.some(v => v === true || String(v).toLowerCase() === 'true'));
      
      // Always update based on the value - form control is the source of truth
      this.checked = isTrue;
      return;
    }
    
    // Handle multiple checkboxes
    // Create a new array to avoid mutating frozen arrays
    // Ensure valueList is an array before spreading
    this.valueList = Array.isArray(valueList) ? [...valueList] : [];
    this.checkboxList = this.checkboxList.map(checkbox => ({ ...checkbox, checked: this.valueList.includes(checkbox.value) }));
  }

  registerOnChange(fn: any) {
    this.onFormChange = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean) {
    this.disabled = disabled;
  }

  handleChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const isChecked = target.checked;
    
    // Handle single checkbox
    if (!this.checkboxList.length) {
      this.checked = isChecked;
      // Pass boolean for Validators.requiredTrue compatibility (e.g. certification checkboxes)
      this.onFormChange(isChecked);
      this.onChange.emit(isChecked);
      return;
    }
    
    // Handle checkbox list
    const value = target.value;
    // Ensure valueList is an array (handle null/undefined cases)
    const currentValueList = Array.isArray(this.valueList) ? this.valueList : [];
    
    // Create a new array instead of mutating the existing one (which might be frozen)
    let newValueList: string[];
    if (isChecked) {
      // Add value if not already present
      newValueList = currentValueList.includes(value) 
        ? [...currentValueList] 
        : [...currentValueList, value];
    } else {
      // Remove value
      newValueList = currentValueList.filter(v => v !== value);
    }
    
    // Update the internal valueList
    this.valueList = newValueList;
    
    // Update form control and emit events
    this.onFormChange(newValueList);
    this.onCheck.emit(this.checkboxList.filter(checkbox => newValueList.includes(checkbox.value)));
  }

  handleBlur() {
    this.onTouched();
  }
}

interface CheckboxOption {
  id: string;
  label: string;
  value: string;
  checked?: boolean;
  disabled?: boolean;
  description?: string;
}